import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import Axios from 'axios'
import { Link } from 'react-router-dom'
import { createSimpleStore } from 'react-simple-reducer'
import api from '../../api'
import { PERMS } from '../../permissions/perms'
import { StoreHome } from '../../stores/homeStore'
import { getUsuarioLogado, hasPerm, useAuthSelector, useAuthState } from '../../stores/authStore'
import { WorkdayPoint } from '../Bi/BiEvolucaoMensal'
import { projetarFechamentoDoMes } from '../Bi/projecao-mensal'

const InicioStore = createSimpleStore(
  {
    artigosTotal: 0,
    realizado: 0,
    projetado: 0,
  },
  {
    setArtigosTotal(state, artigosTotal: number) {
      state.artigosTotal = artigosTotal
    },
    setResumoBi(state, resumo: { realizado: number; projetado: number }) {
      state.realizado = resumo.realizado
      state.projetado = resumo.projetado
    },
  },
  {
    thunks: {
      fetchArtigosTotal() {
        return async (dispatch) => {
          try {
            const { count } = await api.PortalArtigos.get({ limit: 1 })
            dispatch(InicioStore.actions.setArtigosTotal(count))
          } catch {
            dispatch(InicioStore.actions.setArtigosTotal(0))
          }
        }
      },
      fetchResumoBi() {
        return async (dispatch) => {
          try {
            const mesAtual = new Date().toLocaleDateString('en-CA').slice(0, 7)
            const { data } = await Axios.get<{ month: WorkdayPoint[] }>('/matriculas/bi/mensal', {
              params: {
                tipo: 'todos',
                month: mesAtual,
                comparingMonth: mesAtual,
                mode: 'all_days',
              },
            })
            dispatch(
              InicioStore.actions.setResumoBi(
                projetarFechamentoDoMes(data.month, 'valorNewDriverSum')
              )
            )
          } catch {
            dispatch(InicioStore.actions.setResumoBi({ realizado: 0, projetado: 0 }))
          }
        }
      },
    },
  }
)

export function Inicio() {
  return (
    <InicioStore.Provider>
      <LoadDashboard />
      <DashboardLayout>
        <Saudacao />
        <DashboardGrid>
          <CertificadosPendentes />
          <SolicitacoesTurma />
          <ArtigosPortal />
          <VendasMes />
          <PrevisaoMes />
          <AtalhoBi />
        </DashboardGrid>
      </DashboardLayout>
    </InicioStore.Provider>
  )
}

function LoadDashboard() {
  const authState = useAuthState()
  const dispatch = InicioStore.useDispatch()
  const podeVerPortal = hasPerm(authState, PERMS.PORTAL_GERENCIAR)
  const podeVerBi = hasPerm(authState, PERMS.BI_VER)

  useEffect(() => {
    if (podeVerPortal) dispatch(InicioStore.thunks.fetchArtigosTotal())
  }, [dispatch, podeVerPortal])

  useEffect(() => {
    if (podeVerBi) dispatch(InicioStore.thunks.fetchResumoBi())
  }, [dispatch, podeVerBi])

  return null
}

function Saudacao() {
  const usuario = useAuthSelector(getUsuarioLogado)
  return <PageTitle>Olá, {usuario?.nome}</PageTitle>
}

function CertificadosPendentes() {
  const authState = useAuthState()
  const { certificadosPendentesTotal } = StoreHome.useState()
  if (!hasPerm(authState, PERMS.CERTIFICADO_GERENCIAR)) return null

  return (
    <DashboardLink to="/certificados/pendentes">
      <CardTitle>Certificados pendentes</CardTitle>
      <CardValue>{certificadosPendentesTotal}</CardValue>
    </DashboardLink>
  )
}

function SolicitacoesTurma() {
  const authState = useAuthState()
  const { solicitacoesTurmaTotal } = StoreHome.useState()
  if (!hasPerm(authState, PERMS.TURMA_GERENCIAR)) return null

  return (
    <DashboardLink to="/turmas">
      <CardTitle>Solicitações de turma</CardTitle>
      <CardValue>{solicitacoesTurmaTotal}</CardValue>
    </DashboardLink>
  )
}

function ArtigosPortal() {
  const authState = useAuthState()
  const { artigosTotal } = InicioStore.useState()
  if (!hasPerm(authState, PERMS.PORTAL_GERENCIAR)) return null

  return (
    <DashboardLink to="/portal-artigos">
      <CardTitle>Artigos do portal</CardTitle>
      <CardValue>{artigosTotal}</CardValue>
    </DashboardLink>
  )
}

function VendasMes() {
  const authState = useAuthState()
  const { realizado } = InicioStore.useState()
  if (!hasPerm(authState, PERMS.BI_VER)) return null

  return (
    <DashboardCard>
      <CardTitle>Vendas do mês</CardTitle>
      <CardValue>{formatCurrency(realizado)}</CardValue>
    </DashboardCard>
  )
}

function PrevisaoMes() {
  const authState = useAuthState()
  const { projetado } = InicioStore.useState()
  if (!hasPerm(authState, PERMS.BI_VER)) return null

  return (
    <DashboardCard>
      <CardTitle>Previsão do mês</CardTitle>
      <CardValue>{formatCurrency(projetado)}</CardValue>
    </DashboardCard>
  )
}

function AtalhoBi() {
  const authState = useAuthState()
  if (!hasPerm(authState, PERMS.BI_VER)) return null

  return (
    <DashboardLink to="/bi">
      <CardTitle>BI</CardTitle>
      <CardAction>Abrir painel</CardAction>
    </DashboardLink>
  )
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

const DashboardLayout = styled.main`
  min-height: 100%;
  width: 100%;
  padding: 32px;
  background: #f5f6f8;
`

const PageTitle = styled.h1`
  margin: 0 0 24px;
  color: #373737;
  font-size: 24px;
  font-weight: 600;
`

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`

const cardStyles = `
  min-height: 132px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #373737;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const DashboardCard = styled.section`
  ${cardStyles}
`

const DashboardLink = styled(Link)`
  ${cardStyles}
  text-decoration: none;

  &:hover {
    color: #373737;
    border-color: var(--primary-color);
  }
`

const CardTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
`

const CardValue = styled.strong`
  font-size: 28px;
  line-height: 1;
`

const CardAction = styled.span`
  color: var(--primary-color);
  font-weight: 600;
`
